// WebCodecs recorder — writes a real, seekable MP4.
//
// Why this exists instead of MediaRecorder.
//
// MediaRecorder writes a STREAMING container. Its WebM has no Cues index and no
// duration in the header, so players cannot seek it, and Windows' built-in
// players cannot decode VP8/Opus at all. Its MP4 is fragmented, which Windows
// players seek badly. Remuxing afterwards fixes seeking but not the codec
// problem, and needs the whole file in memory — undoing the streaming-to-disk
// design.
//
// Muxing it correctly in the first place fixes all of it:
//   - H.264 + AAC, which every Windows player handles
//   - a proper `moov`, so seeking works
//   - written straight to the file handle, so memory stays flat
//   - frame timestamps we control, so the output is genuinely constant-rate
//
// MediaRecorder stays as the fallback where WebCodecs is missing.

import { Muxer, ArrayBufferTarget, FileSystemWritableFileStreamTarget } from 'mp4-muxer'

type WritableStreamLike = { write(data: unknown): Promise<void>; close(): Promise<void> }

export type Mp4RecorderOptions = {
  canvas: HTMLCanvasElement
  /** Mixed audio (mic + every peer), or null to record video only. */
  audioTrack: MediaStreamTrack | null
  width: number
  height: number
  fps: number
  /** Write target. When absent the file is assembled in memory instead. */
  fileStream: WritableStreamLike | null
}

export type Mp4Recorder = {
  pause(): void
  resume(): void
  /** Flush, finalise the container, and close the file. */
  stop(): Promise<{ buffer?: ArrayBuffer }>
}

/** Chrome/Edge have both; Firefox/Safari coverage is partial, hence the fallback. */
export function supportsWebCodecsRecording(): boolean {
  return (
    typeof window !== 'undefined' &&
    'VideoEncoder' in window &&
    'VideoFrame' in window &&
    'AudioEncoder' in window &&
    'MediaStreamTrackProcessor' in window
  )
}

/** Level rises with resolution; too low and the encoder rejects the config. */
function avcCodecFor(width: number, height: number): string {
  const mb = Math.ceil(width / 16) * Math.ceil(height / 16)
  if (mb > 3600) return 'avc1.4D0033' // Main 5.1 — above 1080p
  if (mb > 1620) return 'avc1.4D0028' // Main 4.0 — up to 1080p
  return 'avc1.4D001F' // Main 3.1 — up to 720p
}

type TrackProcessorCtor = new (init: { track: MediaStreamTrack }) => {
  readable: ReadableStream<AudioData>
}

export async function createMp4Recorder(opts: Mp4RecorderOptions): Promise<Mp4Recorder> {
  const { canvas, audioTrack, width, height, fps, fileStream } = opts

  const arrayTarget = fileStream ? null : new ArrayBufferTarget()
  const muxer = new Muxer({
    target: fileStream
      ? new FileSystemWritableFileStreamTarget(fileStream as never)
      : (arrayTarget as ArrayBufferTarget),
    video: { codec: 'avc', width, height, frameRate: fps },
    audio: audioTrack
      ? { codec: 'aac', sampleRate: 48000, numberOfChannels: 2 }
      : undefined,
    // Interleaved chunks written as they are produced — required for a streaming
    // target, and what keeps memory flat.
    fastStart: fileStream ? false : 'in-memory',
    firstTimestampBehavior: 'offset',
  })

  const videoEncoder = new VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: (e) => console.error('[recording] video encoder', e),
  })
  videoEncoder.configure({
    codec: avcCodecFor(width, height),
    width,
    height,
    framerate: fps,
    // ~2.4 Mbps at 720p20 — plenty for a screen-and-faces composite, and low
    // enough that the encoder never becomes the bottleneck in a live call.
    bitrate: Math.round(width * height * fps * 0.13),
    latencyMode: 'quality',
  })

  // Declared before the audio reader below, which reads `paused`.
  const frameDurationUs = Math.round(1_000_000 / fps)
  let frameIndex = 0
  let paused = false
  let stopped = false
  let timer: ReturnType<typeof setInterval> | null = null
  let keyframeCounter = 0

  let audioEncoder: AudioEncoder | null = null
  let audioReader: ReadableStreamDefaultReader<AudioData> | null = null
  if (audioTrack) {
    audioEncoder = new AudioEncoder({
      output: (chunk, meta) => muxer.addAudioChunk(chunk, meta),
      error: (e) => console.error('[recording] audio encoder', e),
    })
    audioEncoder.configure({
      codec: 'mp4a.40.2',
      sampleRate: 48000,
      numberOfChannels: 2,
      bitrate: 128_000,
    })

    const Processor = (window as unknown as { MediaStreamTrackProcessor: TrackProcessorCtor })
      .MediaStreamTrackProcessor
    audioReader = new Processor({ track: audioTrack }).readable.getReader()
    void (async () => {
      try {
        for (;;) {
          const { done, value } = await audioReader!.read()
          if (done) break
          if (paused || !audioEncoder || audioEncoder.state !== 'configured') { value.close(); continue }
          audioEncoder.encode(value)
          value.close()
        }
      } catch { /* reader cancelled on stop */ }
    })()
  }

  // ── Video pump ────────────────────────────────────────────────────
  // Timestamps come from a frame COUNTER, not the wall clock. That is what makes
  // the file constant-frame-rate: however jittery the draw loop gets, every
  // frame lands exactly 1/fps apart, and players stop stuttering.
  const pump = () => {
    if (stopped || paused) return
    // Never let encoding back up behind a busy main thread — dropping a frame is
    // far better than queueing work the call also needs.
    if (videoEncoder.encodeQueueSize > 2) return
    let frame: VideoFrame
    try {
      frame = new VideoFrame(canvas, { timestamp: frameIndex * frameDurationUs, duration: frameDurationUs })
    } catch {
      return
    }
    // A keyframe every two seconds keeps seeking granular without bloating the file.
    const keyFrame = keyframeCounter % (fps * 2) === 0
    keyframeCounter++
    frameIndex++
    try {
      videoEncoder.encode(frame, { keyFrame })
    } finally {
      frame.close()
    }
  }

  timer = setInterval(pump, 1000 / fps)

  return {
    pause() {
      paused = true
    },
    resume() {
      paused = false
    },
    async stop() {
      stopped = true
      if (timer) { clearInterval(timer); timer = null }
      try { await audioReader?.cancel() } catch { /* already closed */ }
      try { await videoEncoder.flush() } catch { /* nothing pending */ }
      try { await audioEncoder?.flush() } catch { /* nothing pending */ }
      videoEncoder.close()
      audioEncoder?.close()
      muxer.finalize()
      if (fileStream) {
        await fileStream.close()
        return {}
      }
      return { buffer: arrayTarget!.buffer }
    },
  }
}
